import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withRouter } from '../../common/withRouter';
import {
  SimpleDataTable as DataTable,
  Loader,
  ButtonOBSOLETED,
  Notification,
  Message
} from '../../web.common';
import { LayoutContext } from '../layout/siteLayout';
import PageTop from '../layout/PageTop';
import withApi from '../higherOrderComponents/withApi';
import { Filters, FilterInput, FilterSelect, FilterSelectAsync, FilterTimePicker } from './Filters';
import { getQueryStringParameters } from '../../common/utils';
import { mappedSeasonalCourtPurchaseStatus } from '../../enums/seasonalCourtPurchaseStatus';
import { DisplayGuid } from '../items';
import { formatCopenhagen, formatCourtTime } from '../../common/copenhagenTime';

const resultsCount = 15;
const days = ['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const pageConfig = {
  primaryKey: 'id',
  title: 'seasonal court purchases',
  getSearchFunction: api => api.seasonalcourts.searchPurchases,
  columns: [
    {
      property: 'Id',
      label: 'Id',
      render: ({ Id }) => <DisplayGuid>{Id}</DisplayGuid>
    }, {
      property: 'UserName',
      label: 'User Name'
    }, {
      property: 'CourtName',
      label: 'Court Name'
    }, {
      property: 'WeekDay',
      label: 'Week Day',
      render: ({ WeekDay }) => days[WeekDay]
    }, {
      property: 'Time',
      label: 'Time (Copenhagen)',
      render: ({ Time }) => formatCourtTime(Time)
    }, {
      property: 'Status',
      label: 'Status',
      render: ({ Status }) => Status || '-'
    }, {
      property: 'TerminatedAt',
      label: 'TerminatedAt',
      render: ({ TerminatedAt }) => formatCopenhagen(TerminatedAt, { dateOnly: true })
    }
  ],
  filters: [
    {
      name: 'id'
    },
    {
      name: 'userId',
      label: 'User'
    },
    {
      name: 'purchaseId'
    },
    {
      name: 'subscriptionId'
    },
    {
      name: 'seasonalCourtId'
    },
    {
      name: 'status'
    }
  ],
  rowClick: ({ Id }, history) => {
    history.push(`/seasonalcourtpurchase/${Id}`);
  }
};

class SeasonalCourtPurchasesInternal extends React.Component {
  constructor(props) {
    super(props);
    this.state = { isLoading: false };
  }

  componentDidMount() {
  }

  goToPage(number) {
    const { history, location } = this.props;
    const filters = Object.assign(
      {},
      getQueryStringParameters(location.search),
      {
        page: number
      });
    const search = Object.keys(filters)
      .filter(key => key && filters[key])
      .map(key => `${key}=${encodeURIComponent(filters[key])}`).join('&');
    history.push(`?${search}`);
  }

  async loadData(filters, skipPages) {
    const { props: { api } } = this;
    const searchFunction = pageConfig.getSearchFunction(api);

    this.setState({ isLoading: true });
    let result = null;

    try {
      result = await searchFunction({ filters, skipPages, resultsCount });
    }
    catch (e) {
      console.error(`unable to load. ${e}`);
      this.setState({ isLoading: false, errorMessage: new Message('error', 'Unable to load data') });

      return;
    }

    this.setState({ isLoading: false });

    if (Array.isArray(result)) {
      return result;
    }
    return [result];
  }

  propertyToWord(text) {
    const result = text.replace(/([A-Z])/g, ' $1');
    return result.charAt(0).toUpperCase() + result.slice(1);
  }

  defaultValidateForm(formData, updatedKey) {
    const updatedFormData = Object.assign(formData);
    const { primaryKey } = pageConfig;

    if (updatedKey === primaryKey && formData[primaryKey]) {
      const keyValue = updatedFormData[primaryKey];

      const newFormData = {};
      newFormData[primaryKey] = keyValue;

      return newFormData;
    }

    updatedFormData[primaryKey] = null;

    return updatedFormData;
  }

  renderInput(actualType, formData, update, name, label, lookupFunction, options) {
    switch (actualType) {
      case 'Input':
        return (
          <FilterInput
            data={formData}
            update={update}
            name={name}
            placeholder={`Find by ${label}`}
          />
        );
      case 'SelectAsync':
        return (
          <FilterSelectAsync
            className='filter-select'
            data={formData}
            update={update}
            name={name}
            debounce
            loadOptions={lookupFunction}
          />
        );
      case 'TimePicker':
        return (
          <FilterTimePicker
            className='filter-date-picker'
            data={formData}
            update={update}
            name={name}
          />
        );
      case 'Select':
        return (
          <FilterSelect
            className='filter-select'
            data={formData}
            update={update}
            name={name}
            options={options}
          />
        );
      default:
        return 'Unknown Type';
    }
  }

  render() {
    const { location } = this.props;
    const { isLoading, errorMessage } = this.state;
    const parameters = getQueryStringParameters(location.search);
    const pageNumber = parameters.page ? parseInt(parameters.page, 10) : 1;
    const { history } = this.props;

    return (
      <LayoutContext.Consumer>
        {() => (
          <React.Fragment>
            <Notification newMessage={errorMessage} />
            <PageTop
              documentTitle={`${pageConfig.title} page`}
              documentDescription={`${pageConfig.title} page`}
            />

            <h2 className='main-page-header'>{pageConfig.title}</h2>
            <Filters
              initFromUrl
              submitUrl
              validateForm={pageConfig.validateForm || this.defaultValidateForm}
              render={(formData, update) => (
                <div className='filters-block'>
                  {
                    pageConfig.filters.map(({ name, label, type, lookupFunction, options }) => {
                      const actualLabel = label || this.propertyToWord(name);
                      const actualType = type || 'Input';

                      return (
                        <div className='filters-label-block'>
                          <label className='filters-label' htmlFor={name}>
                            {actualLabel}
                          </label>
                          {
                            this.renderInput(actualType, formData, update, name, label, lookupFunction, options)
                          }
                        </div>
                      );
                    })
                  }

                  <div className='filters-submit-wrapper'>
                    <ButtonOBSOLETED type='submit' buttonContent='Search' disabled={isLoading} />
                  </div>
                  <hr />
                </div>
              )}
            />
            <DataTable
              style={{ marginTop: '100px' }}
              loadingContent={(<Loader hasLoaded={false} />)}
              tableConfig={{
                tableName: pageConfig.name,
                rowClasses: 'hover-listview',
                rowClick: data => pageConfig.rowClick(data, history),
                columns: pageConfig.columns
              }}
              getData={async newPageNumber => {
                const filters = getQueryStringParameters(location.search);

                return this.loadData(filters, newPageNumber || 1);
              }}
              paginationData={{
                renderLocation: 'both',
                rowsPerPage: resultsCount,
                pageNumber,
                goToPageNumber: number => this.goToPage(number)
              }}
              skipTableNoResults
              cellVerticalAlign='top'
              hideTableHead={false}
              languageCode='en'
              labels={{
                tableBody: {
                  noResult: <div style={{ display: 'block', textAlign: 'center' }}>No Results</div>
                }
              }}
              cellContent='cellContent'
            />
          </React.Fragment>
        )}
      </LayoutContext.Consumer>
    );
  }
}

const SeasonalCourtPurchases = withRouter(withApi(
  connect(({ user }) => ({ isLoggedIn: !!user }), {})(SeasonalCourtPurchasesInternal)
));

SeasonalCourtPurchases.requiresLogin = true;
SeasonalCourtPurchases.propTypes = { isLoggedIn: PropTypes.bool };

export default SeasonalCourtPurchases;